// Lamtix tenant-dashboard — entry point.
//
// Two code paths, determined before any module is evaluated:
//
//   ?dev (localhost dev OR Cloudflare preview branch build):
//     Dynamically imports DevPreview — contains ONLY screen components.
//     auth.tsx and supabase.ts are NEVER imported on this path.
//
//   Normal (no ?dev, or production build):
//     Dynamically imports App — full auth + Supabase-backed application.
//     Requires VITE_SUPABASE_URL + VITE_SUPABASE_ANON_KEY to be set.
//
// Why dynamic import instead of a top-level import?
//   supabase.ts contains a module-level throw when env vars are absent.
//   A top-level import of App (which transitively imports supabase.ts) would
//   crash the bundle before React can paint → white page. Dynamic import
//   ensures supabase.ts is only evaluated on the App path.
//
// Production safety: __DEV_PREVIEW_BUILD__ is inlined at build time by
//   vite.config.ts. On the production (main) branch it is false; Vite
//   tree-shakes the entire isDevPreview branch — DevPreview and Gallery
//   are never shipped to real users. __DEV_PREVIEW_BUILD__ is true only
//   when Cloudflare Pages builds a non-main branch (preview), using the
//   auto-injected CF_PAGES_BRANCH env var — no dashboard config needed.

import React from 'react';
import ReactDOM from 'react-dom/client';
import { LxErrorState } from '@lamtix/ui';
import { AppErrorBoundary } from './components/app/AppErrorBoundary';
import { installGlobalErrorReporting } from './lib/error-reporting';
import './index.css';

// ── Durable capture for the two crash classes React never sees ────────────────
// The error boundary below catches RENDER crashes. A throw inside an event handler, a timer or
// a rejected promise never reaches it — those land on window, and until now died as a console
// line on the user's own machine. Installed on BOTH entry paths: error-reporting.ts reaches
// supabase through a dynamic import, so this does not drag it into the ?dev preview bundle.
installGlobalErrorReporting();

// ── OREQ-04: a mouse wheel over a FOCUSED number input must not change its value ──
// Owner platform rule (2026-06-14). Number inputs only respond to the wheel while
// focused; blurring on wheel stops the accidental increment/decrement while the page
// still scrolls normally. One global passive listener — no per-input handler needed.
// (Spinner arrows are removed via CSS in index.css.)
document.addEventListener(
  'wheel',
  (e) => {
    const el = e.target as HTMLElement | null;
    if (el instanceof HTMLInputElement && el.type === 'number' && document.activeElement === el) {
      el.blur();
    }
  },
  { passive: true },
);

// ── Stale-chunk self-heal (route code-splitting) ──────────────────────────────
// Deployments are atomic: after one lands, a tab that has been open since the
// previous build still holds an index.html referencing hashed chunk filenames that
// no longer exist. The first navigation into a not-yet-visited screen would then
// 404 its chunk and dead-end. Vite fires 'vite:preloadError' for exactly that.
// Reload once to pick up the fresh index.html; if it fires again within a minute
// this is a real outage rather than staleness, so let the error through to the
// route-level boundary instead of looping on reloads.
window.addEventListener('vite:preloadError', (event) => {
  const KEY = 'lx-chunk-reload-at';
  const last = Number(sessionStorage.getItem(KEY) ?? '0');
  if (Date.now() - last < 60_000) return; // second failure → AppErrorBoundary takes over
  sessionStorage.setItem(KEY, String(Date.now()));
  event.preventDefault(); // we recover by reloading, not by throwing
  window.location.reload();
});

const rootEl = document.getElementById('root') as HTMLElement;

// Last-resort fallback if the dynamic import itself fails (e.g. a stale/blocked chunk
// after a deploy). Without this the promise rejects unhandled and the page stays blank
// — the same white-screen failure mode the AppErrorBoundary fixes for render crashes.
function renderFatalLoadError(error: unknown): void {
  // eslint-disable-next-line no-console
  console.error('[main] failed to load the application bundle:', error);
  ReactDOM.createRoot(rootEl).render(
    <React.StrictMode>
      <div dir="rtl" style={{ maxWidth: 560, margin: '64px auto', padding: '0 20px' }}>
        <LxErrorState
          locale="ar"
          titleAr="تعذّر تحميل التطبيق"
          titleEn="Couldn't load the application"
          bodyAr="قد تكون نسخة قديمة محفوظة في المتصفّح. أعد التحميل للحصول على أحدث إصدار."
          bodyEn="A stale cached version may be loaded. Reload to fetch the latest build."
          onRetry={() => window.location.reload()}
        />
      </div>
    </React.StrictMode>,
  );
}

// Gate: localhost dev OR Cloudflare preview branch (auto via CF_PAGES_BRANCH).
// __DEV_PREVIEW_BUILD__ is resolved at build time — see vite.config.ts.
const isDevPreview =
  (import.meta.env.DEV || __DEV_PREVIEW_BUILD__) &&
  new URLSearchParams(window.location.search).has('dev');

if (isDevPreview) {
  void import('./DevPreview')
    .then(({ DevPreview }) => {
      ReactDOM.createRoot(rootEl).render(
        <React.StrictMode>
          <AppErrorBoundary>
            <DevPreview />
          </AppErrorBoundary>
        </React.StrictMode>,
      );
    })
    .catch(renderFatalLoadError);
} else {
  void import('./App')
    .then(({ default: App }) => {
      ReactDOM.createRoot(rootEl).render(
        <React.StrictMode>
          <AppErrorBoundary>
            <App />
          </AppErrorBoundary>
        </React.StrictMode>,
      );
    })
    .catch(renderFatalLoadError);
}
